import * as THREE from 'three';
import type { City, Place } from '../world/city';
import type { LoadingBay } from '../world/logistics';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import { Reservations } from './reservations';

interface Courier {
  car: TrafficVehicle;
  person: Person;
  hub: LoadingBay;
  bay: LoadingBay;
  destination?: LoadingBay;
  source?: Place;
  shop?: Place;
  cargo: number;
  delivered: number;
  trips: number;
  state:
    | 'waiting'
    | 'collecting'
    | 'loading'
    | 'outbound'
    | 'unloading'
    | 'delivering'
    | 'return-worker'
    | 'returning'
    | 'restocking'
    | 'interrupted'
    | 'unavailable';
  reason: string;
  retry: number;
  task: string;
  sequence: number;
  aboard: boolean;
  blockedTime: number;
}

/** Finite original couriers move conserved goods through represented rooms.
 * Driving and walking share the normal grounded, collision-aware controllers. */
export class Logistics {
  fleet: Courier[] = [];
  readonly reservations: Reservations;
  log: { time: number; courier: string; action: string; cargo: number; place?: string }[] = [];
  time = 0;
  delivered = 0;
  collected = 0;
  returned = 0;
  interrupted = 0;
  private epoch = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reservations = new Reservations(() => physics.life.epoch);
    this.reset();
  }
  clear(): void {
    for (const u of this.fleet) this.population.civil?.cancelTask(u.person, 'Delivery lifecycle cleared');
    this.fleet = [];
    this.reservations.clear();
    this.log = [];
    this.time = this.delivered = this.collected = this.returned = this.interrupted = 0;
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    for (const [i, hub] of (this.city.logistics?.hubs ?? []).entries()) {
      const car = this.population.createCar(`delivery-van:${i}`, 'delivery', hub.park, hub.from),
        person = this.population.createCrew(`crew:delivery:${i}`, hub.worker);
      car.name = `${hub.name} · parcel van`;
      car.controlled = true;
      car.state = 'deployed';
      car.speed = 5.5;
      car.yaw = Math.atan2(-hub.direction.x, -hub.direction.z);
      car.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), car.yaw);
      car.previousQ.copy(car.q);
      car.body.setRotation(car.q, true);
      person.name = `Courier ${i + 1} · ${hub.name}`;
      person.layer = 'street';
      person.color = [0xb5b38b, 0xa68b69, 0x859d98][i % 3];
      person.carry = '';
      this.fleet.push({
        car,
        person,
        hub,
        bay: hub,
        cargo: 0,
        delivered: 0,
        trips: 0,
        state: 'waiting',
        reason: 'Waiting for shop stock demand',
        retry: 3 + i * 11,
        task: '',
        sequence: 0,
        aboard: false,
        blockedTime: 0,
      });
    }
  }
  open(bay: LoadingBay): boolean {
    const surface = this.city.infrastructure?.find((b) => b.id === bay.surface);
    const owner = surface?.cells.find(
      (c) => c.x === Math.floor(surface.w / 2) && c.z === Math.floor(surface.d / 2),
    );
    const cluster = owner ? this.physics.cellOwner.get(owner.id) : undefined;
    return (
      !!this.population.civil?.network.available(bay.worker) &&
      cluster !== undefined &&
      !!(this.physics.clusters.get(cluster) ?? this.physics.dormant.get(cluster))?.anchored
    );
  }
  private record(u: Courier, action: string, place?: Place): void {
    this.log.push({ time: this.time, courier: u.person.id, action, cargo: u.cargo, place: place?.id });
    if (this.log.length > 384) this.log.shift();
    u.reason = action;
  }
  private task(u: Courier, place: Place, action: string, duration: number, done: () => void): void {
    const civil = this.population.civil!;
    const access = this.city.logistics?.servicePoints[place.id];
    if (access && !civil.network.available(place)) {
      this.abort(u, 'Stock destination or its support is unavailable');
      return;
    }
    if (access) place = this.city.places!.find((p) => p.id === access)!;
    if (!u.task) {
      const token = `${u.person.id}:${u.sequence}:${u.state}`;
      if (!civil.assignTask(u.person, place, token, action, duration)) {
        u.retry = 2;
        u.reason = 'Waiting for an available task interaction';
        if (!civil.network.available(place)) this.abort(u, 'Delivery destination or support disrupted');
        return;
      }
      civil.routines.get(u.person.id)!.role = 'delivery';
      u.task = token;
      this.record(u, action, place);
    }
    const task = civil.task(u.person);
    if (task?.token !== u.task) return;
    if (task.state === 'failed') this.abort(u, task.reason);
    else if (task.state === 'completed') {
      u.task = '';
      done();
    }
  }
  private aboard(u: Courier): void {
    u.aboard = true;
    u.person.controlled = true;
    u.person.hidden = true;
    u.person.carry = '';
    u.person.state = 'driving delivery';
  }
  private leave(u: Courier): boolean {
    const door = u.bay.worker;
    if (!this.open(u.bay) || !this.population.footClear(u.person, door)) return false;
    const ground = this.physics.ground(door, 0.35, 0.5, u.car.body);
    if (!ground) return false;
    u.aboard = false;
    u.person.hidden = false;
    u.person.controlled = true;
    u.person.p.set(door.x, ground.y, door.z);
    u.person.previous.copy(u.person.p);
    u.person.layer = 'street';
    u.person.state = 'unloading';
    u.person.carry = u.cargo ? 'parcel' : '';
    u.car.doorOpen = 1;
    this.population.audio.play('door', 0.5, u.car.p);
    return true;
  }
  private drive(u: Courier, bay: LoadingBay): boolean {
    if (!this.open(bay)) return false;
    const plan = this.population.plannedRoute(u.car, { ...bay.lane, edge: bay.edge }, bay.from, false);
    if (!plan.length) return false;
    // A shallow approach enters the reserved apron while retaining the road
    // heading. Car/pedestrian sweeps still apply throughout the driveway.
    const last = plan.at(-1)!;
    last.x -= bay.direction.x * 8;
    last.z -= bay.direction.z * 8;
    last.node = undefined;
    last.maxSpeed = 1.6;
    plan.push({
      x: bay.park.x - bay.direction.x * 4,
      y: bay.park.y,
      z: bay.park.z - bay.direction.z * 4,
      edge: bay.edge,
      maxSpeed: 1.6,
    });
    plan.push({ ...bay.park, edge: bay.edge, maxSpeed: 1.6 });
    u.car.plan = plan;
    u.car.parkingYaw = Math.atan2(-bay.direction.x, -bay.direction.z);
    u.car.state = 'driving';
    u.car.blockedFor = 0;
    u.car.targetSpeed = 0;
    u.car.doorOpen = 0;
    u.bay = bay;
    this.aboard(u);
    return true;
  }
  private order(u: Courier): void {
    const civil = this.population.civil!;
    const sources = u.hub.destinations
      .map((id) => this.city.places!.find((p) => p.id === id)!)
      .filter((p) => p && civil.network.available(p) && (civil.inventory.get(p.id) ?? 0) > 0);
    if (!sources.length) {
      u.reason = 'Warehouse stock exhausted or access unavailable';
      u.retry = 12;
      return;
    }
    const stops = [...(this.city.logistics?.stops ?? [])]
      .filter((b) => this.open(b))
      .sort(
        (a, b) =>
          Math.hypot(a.park.x - u.car.p.x, a.park.z - u.car.p.z) -
          Math.hypot(b.park.x - u.car.p.x, b.park.z - u.car.p.z),
      );
    for (const bay of stops) {
      const shop = bay.destinations
        .map((id) => this.city.places!.find((p) => p.id === id)!)
        .find(
          (p) =>
            p &&
            civil.network.available(p) &&
            (civil.inventory.get(p.id) ?? 30) < 30 &&
            !this.reservations.holders(`order:${p.id}`, this.time).length,
        );
      if (!shop || !this.reservations.acquire(bay.id, u.car.id, this.time, 1, 180)) continue;
      this.reservations.acquire(`order:${shop.id}`, u.car.id, this.time, 1, 180);
      u.shop = shop;
      u.source = sources[0];
      u.destination = bay;
      u.sequence++;
      u.state = 'collecting';
      this.record(u, `Collecting stock for ${shop.name}`, shop);
      return;
    }
    u.reason = 'Waiting for shop stock demand or a free loading bay';
    u.retry = 8;
  }
  private abort(u: Courier, reason: string): void {
    this.population.civil?.cancelTask(u.person, reason);
    this.reservations.release(u.car.id);
    u.task = '';
    u.sequence++;
    u.state = 'interrupted';
    u.retry = 8;
    this.interrupted++;
    this.record(u, reason);
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch || !this.population.civil) return;
    this.time += dt;
    const civil = this.population.civil;
    for (const u of this.fleet) {
      u.retry -= dt;
      if (u.state === 'unavailable') continue;
      if (u.car.state === 'wreck' || u.person.state === 'incapacitated') {
        if (u.aboard) {
          u.person.hidden = false;
          u.person.controlled = false;
          u.person.p.copy(u.car.p);
          u.person.previous.copy(u.person.p);
          u.person.state = 'incapacitated';
          u.person.velocity.copy(u.car.body.linvel());
          u.aboard = false;
        }
        this.abort(u, 'Delivery interrupted; original crew and cargo remain at the incident');
        u.state = 'unavailable';
        continue;
      }
      if (u.person.state === 'falling') {
        u.reason = 'Waiting for the original courier to regain support';
        continue;
      }
      if (u.aboard) {
        u.person.previous.copy(u.person.p);
        u.person.p.copy(u.car.p).add(new THREE.Vector3(0, 0.85, 0));
        u.person.hidden = u.person.controlled = true;
      }
      u.blockedTime = u.car.blockedBy && u.car.actualSpeed < 0.1 ? u.blockedTime + dt : 0;
      if (u.destination && !['waiting', 'interrupted', 'unavailable'].includes(u.state)) {
        this.reservations.acquire(u.destination.id, u.car.id, this.time, 1, 180);
        if (u.shop) this.reservations.acquire(`order:${u.shop.id}`, u.car.id, this.time, 1, 180);
      }
      if (u.retry > 0) continue;
      if (u.state === 'waiting') this.order(u);
      if (u.state === 'collecting')
        this.task(u, u.source!, 'collecting parcels', 3, () => {
          u.cargo = Math.min(
            6,
            civil.inventory.get(u.source!.id) ?? 0,
            30 - (civil.inventory.get(u.shop!.id) ?? 30),
          );
          if (u.cargo <= 0) {
            this.abort(u, 'Stock demand changed before collection');
            return;
          }
          civil.inventory.set(u.source!.id, civil.inventory.get(u.source!.id)! - u.cargo);
          this.collected += u.cargo;
          u.person.carry = 'parcel';
          u.state = 'loading';
          this.record(u, 'Collected finite warehouse stock', u.source);
        });
      if (u.state === 'loading')
        this.task(u, u.hub.worker, 'loading parcels', 3, () => {
          if (this.drive(u, u.destination!)) {
            u.state = 'outbound';
            this.record(u, 'Loaded original courier and cargo; departing for shop');
          } else {
            u.retry = 5;
            u.reason = 'Waiting for a safe outbound road route';
          }
        });
      if (u.state === 'outbound' || u.state === 'returning') {
        if (!this.open(u.bay)) {
          this.abort(u, 'Loading bay closed after damage');
          continue;
        }
        if (
          u.car.state === 'deployed' &&
          u.car.p.distanceTo(new THREE.Vector3(u.bay.park.x, u.bay.park.y, u.bay.park.z)) < 1.3 &&
          this.leave(u)
        ) {
          if (u.state === 'outbound') {
            u.state = 'unloading';
            this.record(u, 'Parked clear of the lane; unloading for shop');
          } else if (u.cargo && u.source && civil.network.available(u.source)) u.state = 'restocking';
          else {
            u.state = 'waiting';
            u.retry = 10;
            u.trips++;
            u.destination = undefined;
            u.shop = undefined;
            this.record(u, 'Returned original courier and van to warehouse');
          }
        } else if (u.blockedTime > 12) {
          this.drive(u, u.bay);
          u.blockedTime = 0;
          u.retry = 5;
          u.reason = 'Road obstruction; waiting or planning a legal alternative';
        }
      }
      if (u.state === 'unloading')
        this.task(u, u.bay.worker, 'unloading parcels', 3, () => {
          u.state = 'delivering';
        });
      if (u.state === 'delivering')
        this.task(u, u.shop!, 'restocking shelves', 4, () => {
          const moved = Math.min(u.cargo, 30 - (civil.inventory.get(u.shop!.id) ?? 30));
          civil.inventory.set(u.shop!.id, (civil.inventory.get(u.shop!.id) ?? 0) + moved);
          u.cargo -= moved;
          u.delivered += moved;
          this.delivered += moved;
          u.person.carry = u.cargo ? 'parcel' : '';
          u.state = 'return-worker';
          this.record(u, `Delivered ${moved} conserved items inside the shop`, u.shop);
        });
      if (u.state === 'return-worker')
        this.task(u, u.bay.worker, 'boarding delivery van', 1.5, () => {
          if (u.bay === u.hub) {
            u.state = 'waiting';
            u.retry = 10;
            u.trips++;
            u.destination = undefined;
            u.shop = undefined;
            this.reservations.release(u.car.id);
            this.record(u, 'Returned original courier and van to warehouse');
            return;
          }
          if (this.drive(u, u.hub)) {
            u.state = 'returning';
            this.reservations.release(u.car.id);
            this.record(u, 'Worker returned through the entrance; returning van to warehouse');
          } else {
            u.retry = 5;
            u.reason = 'Waiting for a safe road route home';
          }
        });
      if (u.state === 'restocking')
        this.task(u, u.source!, 'returning warehouse stock', 3, () => {
          civil.inventory.set(u.source!.id, (civil.inventory.get(u.source!.id) ?? 0) + u.cargo);
          this.returned += u.cargo;
          u.cargo = 0;
          u.person.carry = '';
          u.state = 'return-worker';
          this.record(u, 'Undelivered goods returned to the original source', u.source);
        });
      if (u.state === 'interrupted') {
        if (u.aboard) {
          if (this.drive(u, u.hub)) u.state = 'returning';
        } else if (!['flee', 'incapacitated', 'falling'].includes(u.person.state) && this.open(u.bay))
          u.state = 'return-worker';
        u.retry = 5;
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      deliveryFleet: this.fleet.length,
      deliveryItemsCollected: this.collected,
      deliveryItemsDelivered: this.delivered,
      deliveryItemsReturned: this.returned,
      deliveryCargo: this.fleet.reduce((n, u) => n + u.cargo, 0),
      deliveryTrips: this.fleet.reduce((n, u) => n + u.trips, 0),
      deliveryInterruptions: this.interrupted,
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      fleet: this.fleet.map((u) => ({
        id: u.car.id,
        worker: u.person.id,
        state: u.state,
        reason: u.reason,
        position: u.car.p.toArray(),
        workerPosition: u.person.p.toArray(),
        bay: u.bay.id,
        destination: u.shop?.id,
        cargo: u.cargo,
        delivered: u.delivered,
        trips: u.trips,
        aboard: u.aboard,
        blocked: u.car.blockedBy,
      })),
      log: this.log,
    };
  }
}
